<template>
  <div
    ref="dropdownMenu"
    :class="{
      'is-open': isOpen,
    }"
    class="calculator-options"
  >
    <div
      class="calculator-options-toggle"
      @click="handleOpen"
    >
      <div class="calculator-options-toggle__button">
        <span class="calculator-options-toggle__currency">
          {{ selected }}
        </span>
      </div>
      <span class="calculator-options-toggle__icon">
        <ChevronDownIcon/>
      </span>
    </div>

    <div ref="dropdownRef" class="calculator-options-dropdown">
      <div class="calculator-options-dropdown-wrapper">
        <div class="calculator-options-dropdown-header">
          <div class="calculator-options-dropdown-header__side"></div>
          <div class="calculator-options-dropdown-header__title">
            {{ dropdownTitle }}
          </div>
          <div class="calculator-options-dropdown-header__side calculator-options-dropdown-header__close" @click="handleOpen">
            <CircleCloseIcon />
          </div>
        </div>

        <div class="calculator-options-search">
          <input
            type="text"
            :placeholder="$t('calculator.searchPlaceholder')"
            class="calculator-options-search__input"
            :value="searchString"
            @input="searchString = $event.target.value"
          />
          <div v-show="searchString.length > 0"
            class="calculator-options-search__clear"
            @click="searchString = ''"
          >
            <CloseIcon/>
          </div>
        </div>

        <PbScroll v-if="items.length > 0">
          <ul class="calculator-options-list">
            <li v-for="item in filteredItems" :key="item.code"
              class="calculator-options-item"
              @click="handleSelect(item)"
            >
              <CryptoCurrency v-if="isCrypto"
                :selected="item.code === selected"
                :currency="item.code"
                :icon="item.code.toUpperCase()"
                :label="item.name"
              />
              <FiatCurrency v-else
                :selected="item.code === selected"
                :currency="item.code"
                :label="item.name"
              />
            </li>
          </ul>
        </PbScroll>
        <div v-else class="calculator-options-loader">
          <ExchangeLoading />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CryptoIcon from '@/components/Prices/CryptoIcon.vue';
import Icon from '@/components/Shared/Icon.vue';
import PbScroll from "@kano/components/ExchangeFormV2/PbScroll.vue";
import { getPaybisIconsUrl } from "@/helpers/cdnHelper";
import CryptoCurrency from "@kano/components/ExchangeFormV2/CryptoCurrency.vue";
import FiatCurrency from "@kano/components/ExchangeFormV2/FiatCurrency.vue";
import ExchangeLoading from "@kano/components/ExchangeFormV2/ExchangeLoading.vue";
import { freezeBody } from "@kano/utilities/FreezeBody";
import ChevronDownIcon from '@kanoAssets/images/icons/chevron-down.svg';
import CircleCloseIcon from '@kanoAssets/images/icons/circle-close.svg';
import CloseIcon from '@kanoAssets/images/icons/close.svg';

export default {
  name: 'CalculatorInputOptions',

  components: {
    ExchangeLoading,
    FiatCurrency,
    CryptoCurrency,
    PbScroll,
    CryptoIcon,
    Icon,
    ChevronDownIcon,
    CircleCloseIcon,
    CloseIcon,
  },

  props: {
    items: { type: Array, required: true },
    selectedOption: { type: String, required: true },
    isCrypto: { type: Boolean, required: true },
  },

  data () {
    return {
      isOpen: false,
      selected: this.selectedOption,
      searchString: '',
    };
  },

  watch: {
    selectedOption () {
      this.selected = this.selectedOption;
    },
    isOpen (val) {
      if (window.innerWidth < 1000) {
        freezeBody(val);
      } else {
        freezeBody(false, false);
      }
    },
  },

  destroyed () {
    document.removeEventListener('click', this.handleCloseAll);
  },

  computed: {
    filteredItems () {
      return this.items.filter((item) => {
        return item.name.toLowerCase().includes(this.searchString.toLowerCase()) ||
          item.code.toLowerCase().includes(this.searchString.toLowerCase());
      });
    },
    dropdownTitle () {
      const titleMessage = this.isCrypto ? 'crypto-title' : 'fiat-title';
      return this.$i18n.t(`calculator.form.drop-down.${titleMessage}`);
    },
  },

  methods: {
    getPaybisIconsUrl,
    getLabel (item) {
      return this.isCrypto ? item.name : item.code;
    },

    getDetails (item) {
      return this.isCrypto ? item.code : item.name;
    },

    handleOpen () {
      this.isOpen = !this.isOpen;

      if (!this.isOpen) {
        document.removeEventListener('click', this.handleCloseAll);

        return;
      }

      document.addEventListener('click', this.handleCloseAll);
      this.$emit('is-open');
    },

    handleClose () {
      this.isOpen = false;
      this.searchString = '';
      document.removeEventListener('click', this.handleCloseAll);
    },

    handleCloseAll (e) {
      const el = this.$refs.dropdownMenu;
      const target = e.target;

      if (el !== target && !el.contains(target)) {
        this.handleClose();
      }
    },

    handleSelect (currency) {
      this.selected = currency.code;
      this.$emit('change', currency);
      this.handleClose();
    },
  },
};
</script>

<style scoped lang="scss">
@use '@kanoAssets/styles/breakpoints.scss';

.calculator-options.is-open:before {
  content: '';
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 99;
  background-color: var(--color-base);

  @include breakpoints.desktop {
    display: none;
  }
}

.calculator-options-dropdown {
  display: none;
  min-width: 100%;
  padding: 24px 0;
  position: fixed;
  top: 24px;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
  border-color: var(--color-base-0);
  border-radius: 16px 16px 0 0;
  background-color: var(--color-base-0);
  flex-direction: column;
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
  transition: opacity 0.2s linear, transform 0.2s linear,
  visibility 0s linear 0s;

  .is-open & {
    display: flex;
  }

  @include breakpoints.desktop {
    box-shadow: 0 0 24px var(--color-base-16);
    border-radius: 12px;
    background-color: var(--color-base-0);
    position: absolute;
    top: calc(100% - 34px);
    left: 50%;
    transform: translateX(-50%);
    z-index: 10;
    min-height: 480px;
    min-width: 480px;
    overflow: hidden;

    .is-open & {
      display: block;
    }
  }

  @media screen and (max-height: 500px) {
    height: calc(100vh - 32px);
  }
}

.calculator-options-dropdown-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.calculator-options-dropdown-header {
  display: flex;
  align-items: center;
  padding: 0 24px;
  min-height: 30px;
  margin-bottom: 32px;
  justify-content: stretch;
  flex: 0 0 auto;

  @include breakpoints.desktop {
    margin-bottom: 22px;
  }

  &__side {
    flex: 1 1 0;
    display: flex;
    align-items: center;
  }

  &__title {
    font-size: 20px;
    line-height: 28px;
    font-weight: 600;
  }

  &__close {
    justify-content: end;

    & svg {
      cursor: pointer;
    }
  }
}

.calculator-options-search {
  margin-bottom: 34px;
  position: relative;

  @include breakpoints.desktop {
    margin-bottom: 20px;
  }

  &__input {
    font-size: 16px;
    background: var(--color-base-0);
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    padding-left: 24px;
    padding-right: 24px;
    width: 100%;
    outline: none;
    height: 36px;
    transition: border-color 0.2s ease-in-out;

    ::placeholder {
      font-size: 16px;
      line-height: 19px;
      color: var(--color-base-48);
    }

    &:focus {
      border-color: var(--color-primary);
    }
  }

  &__clear {
    position: absolute;
    right: 28px;
    top: 10px;
    cursor: pointer;
  }
}

.calculator-options-list {
  margin: 0 14px;
  max-height: 480px;
}

.calculator-options-loader {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
